import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getExtensionValue } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Accordion, Group, Paper, Stack, Title } from "@mantine/core";

import { IconNotes } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { NEWBORN_POSTPARTUM_PROCEDURES } from "@/encounters/constants/newborn-postpartum";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Extensions } from "@/fhir/enums/extensions";
import {
  NewbornProceduresParentQuestionnaire,
  NewbornProceduresQuestionnaires,
} from "@/fhir/enums/questionnaires";

type ProcedureKey = keyof typeof NewbornProceduresQuestionnaires;

function getProcedureTitle(key: ProcedureKey) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function getProcedureAnswers(questionnaireResponse?: QuestionnaireResponse) {
  if (!questionnaireResponse?.item?.[0])
    return {
      performed: false,
      declined: false,
      notIndicated: false,
      notes: "",
      hasData: false, // Flag to indicate if any data was recorded
    };
  const section = questionnaireResponse.item[0];
  const sectionLinkId = section.linkId;
  const find = (linkId: string) => section.item?.find((item) => item.linkId === linkId);
  // Notes is at the top level of the questionnaire, not nested under the section
  const notesItem = questionnaireResponse.item.find((item) => item.linkId === "notes");
  return {
    performed: find(`${sectionLinkId}Performed`)?.answer?.[0]?.valueBoolean ?? false,
    declined: find(`${sectionLinkId}Declined`)?.answer?.[0]?.valueBoolean ?? false,
    notIndicated: find(`${sectionLinkId}NotIndicated`)?.answer?.[0]?.valueBoolean ?? false,
    notes: notesItem?.answer?.[0]?.valueString || "",
    hasData: true, // Flag to indicate data was recorded
  };
}

const booleanToYesOrNo = (value: boolean): string => {
  return value ? "Yes" : "No";
};

const getFieldDisplayValue = (
  answers: ReturnType<typeof getProcedureAnswers>,
  field: "performed" | "declined" | "notIndicated",
): string => {
  if (!answers.hasData) {
    return "Not answered";
  }
  return booleanToYesOrNo(answers[field]);
};

const getProcedureStatus = (answers: ReturnType<typeof getProcedureAnswers>): string => {
  if (answers.performed) return "Performed";
  if (answers.declined) return "Declined";
  if (answers.notIndicated) return "Not Indicated";
  return "Not answered";
};

const NewbornProceduresDiff = ({
  currentVersionIndex,
  versionId,
}: {
  currentVersionIndex: number;
  versionId?: string;
}) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetching, setIsFetching] = useState(true);
  const [histories, setHistories] = useState<Record<ProcedureKey, QuestionnaireResponse[]>>(
    {} as Record<ProcedureKey, QuestionnaireResponse[]>,
  );
  const [procedureNotesHistory, setProcedureNotesHistory] = useState<QuestionnaireResponse[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsFetching(true);
      const all: Record<ProcedureKey, QuestionnaireResponse[]> = {} as Record<
        ProcedureKey,
        QuestionnaireResponse[]
      >;
      await Promise.all([
        // Fetch individual procedure histories
        ...NEWBORN_POSTPARTUM_PROCEDURES.map(async (key) => {
          const url = NewbornProceduresQuestionnaires[key as ProcedureKey];
          const [history] = await fetchAmendmentHistoryByQuestionnaireUrl(
            medplum,
            url,
            encounterId!,
          );
          all[key as ProcedureKey] = history || [];
        }),
        // Fetch procedure notes history
        (async () => {
          const [history] = await fetchAmendmentHistoryByQuestionnaireUrl(
            medplum,
            NewbornProceduresParentQuestionnaire.Base,
            encounterId!,
          );
          if (mounted) {
            setProcedureNotesHistory(history || []);
          }
        })(),
      ]);
      if (mounted) {
        setHistories(all);
        setIsFetching(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentVersionIndex, encounterId, medplum, versionId]);

  if (isFetching) return <Loading />;

  // Check if there's any procedure data in either current or previous versions
  const hasAnyData =
    NEWBORN_POSTPARTUM_PROCEDURES.some((key) => {
      const history = histories[key as ProcedureKey] || [];

      if (versionId) {
        // Try extension-based lookup first
        const currentIndex = history.findIndex((qr) => {
          const parentVersionId = getExtensionValue(
            qr,
            Extensions.QuestionnaireResponseParentVersionId,
          );
          return parentVersionId === versionId;
        });

        if (currentIndex === -1) {
          // Extension-based lookup failed, fall back to index-based lookup
          const current = history[currentVersionIndex];
          const previous = history[currentVersionIndex + 1];
          return current || previous;
        } else {
          // Extension-based lookup succeeded
          const current = history[currentIndex];
          const previous = history[currentIndex + 1];
          return current || previous;
        }
      } else {
        // Use index-based lookup
        const current = history[currentVersionIndex];
        const previous = history[currentVersionIndex + 1];
        return current || previous;
      }
    }) ||
    (() => {
      // Also check for procedure notes
      if (versionId) {
        const currentIndex = procedureNotesHistory.findIndex((qr) => {
          const parentVersionId = getExtensionValue(
            qr,
            Extensions.QuestionnaireResponseParentVersionId,
          );
          return parentVersionId === versionId;
        });

        if (currentIndex === -1) {
          const current = procedureNotesHistory[currentVersionIndex];
          const previous = procedureNotesHistory[currentVersionIndex + 1];
          return current || previous;
        } else {
          const current = procedureNotesHistory[currentIndex];
          const previous = procedureNotesHistory[currentIndex + 1];
          return current || previous;
        }
      } else {
        const current = procedureNotesHistory[currentVersionIndex];
        const previous = procedureNotesHistory[currentVersionIndex + 1];
        return current || previous;
      }
    })();

  if (!hasAnyData) {
    return (
      <Paper p="lg" withBorder>
        <Title mb="lg" order={2}>
          Newborn Procedures
        </Title>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No procedure data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  return (
    <Paper p="lg" withBorder>
      <Title mb="lg" order={2}>
        Newborn Procedures
      </Title>
      <Accordion multiple>
        {NEWBORN_POSTPARTUM_PROCEDURES.map((key) => {
          const history = histories[key as ProcedureKey] || [];

          let current: QuestionnaireResponse | undefined;
          let previous: QuestionnaireResponse | undefined;

          if (versionId) {
            // Try extension-based lookup first
            const currentIndex = history.findIndex((qr) => {
              const parentVersionId = getExtensionValue(
                qr,
                Extensions.QuestionnaireResponseParentVersionId,
              );
              return parentVersionId === versionId;
            });

            if (currentIndex === -1) {
              // Extension-based lookup failed, fall back to index-based lookup
              current = history[currentVersionIndex];
              previous = history[currentVersionIndex + 1];
            } else {
              // Extension-based lookup succeeded
              current = history[currentIndex];
              previous = history[currentIndex + 1];
            }
          } else {
            // Use index-based lookup
            current = history[currentVersionIndex];
            previous = history[currentVersionIndex + 1];
          }

          if (!current && !previous) return null;

          let currentAns, previousAns;
          if (!current && !previous) {
            currentAns = previousAns = {
              performed: false,
              declined: false,
              notIndicated: false,
              notes: "",
              hasData: false,
            };
          } else {
            currentAns = getProcedureAnswers(current);
            previousAns = getProcedureAnswers(previous);
            // If only one exists, show default values for the missing one
            if (!current) {
              currentAns = {
                performed: false,
                declined: false,
                notIndicated: false,
                notes: "",
                hasData: false,
              };
            }
            if (!previous) {
              previousAns = {
                performed: false,
                declined: false,
                notIndicated: false,
                notes: "",
                hasData: false,
              };
            }
          }

          return (
            <Accordion.Item key={key} value={key}>
              <Accordion.Control>
                <Group>
                  <IconNotes size={24} color="#a0a0a0" style={{ marginRight: 8 }} />
                  <TextWrapper c="charcoal.0" variant="bold">
                    {getProcedureTitle(key as ProcedureKey)}
                  </TextWrapper>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Group grow align="stretch">
                  <Paper p="md" withBorder>
                    <Stack>
                      <Title mb="lg" order={4} c="gray.6">
                        Previous
                      </Title>
                      <Stack gap="md">
                        <Stack gap="0">
                          <TextWrapper fw={600}>Status</TextWrapper>
                          <WordsDiff
                            showRemoved
                            current={getProcedureStatus(currentAns)}
                            previous={getProcedureStatus(previousAns)}
                          />
                        </Stack>
                        <Stack gap="0">
                          <TextWrapper fw={600}>Performed</TextWrapper>
                          <WordsDiff
                            showRemoved
                            current={getFieldDisplayValue(currentAns, "performed")}
                            previous={getFieldDisplayValue(previousAns, "performed")}
                          />
                        </Stack>
                        <Stack gap="0">
                          <TextWrapper fw={600}>Declined</TextWrapper>
                          <WordsDiff
                            showRemoved
                            current={getFieldDisplayValue(currentAns, "declined")}
                            previous={getFieldDisplayValue(previousAns, "declined")}
                          />
                        </Stack>
                        <Stack gap="0">
                          <TextWrapper fw={600}>Not Indicated</TextWrapper>
                          <WordsDiff
                            showRemoved
                            current={getFieldDisplayValue(currentAns, "notIndicated")}
                            previous={getFieldDisplayValue(previousAns, "notIndicated")}
                          />
                        </Stack>
                        {(previousAns.notes || currentAns.notes) && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Notes</TextWrapper>
                            <WordsDiff
                              showRemoved
                              current={currentAns.notes}
                              previous={previousAns.notes}
                            />
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                  <Paper p="md" withBorder>
                    <Stack>
                      <Title mb="lg" order={4} c="gray.6">
                        Amended
                      </Title>
                      <Stack gap="md">
                        <Stack gap="0">
                          <TextWrapper fw={600}>Status</TextWrapper>
                          <WordsDiff
                            current={getProcedureStatus(currentAns)}
                            previous={getProcedureStatus(previousAns)}
                          />
                        </Stack>
                        <Stack gap="0">
                          <TextWrapper fw={600}>Performed</TextWrapper>
                          <WordsDiff
                            current={getFieldDisplayValue(currentAns, "performed")}
                            previous={getFieldDisplayValue(previousAns, "performed")}
                          />
                        </Stack>
                        <Stack gap="0">
                          <TextWrapper fw={600}>Declined</TextWrapper>
                          <WordsDiff
                            current={getFieldDisplayValue(currentAns, "declined")}
                            previous={getFieldDisplayValue(previousAns, "declined")}
                          />
                        </Stack>
                        <Stack gap="0">
                          <TextWrapper fw={600}>Not Indicated</TextWrapper>
                          <WordsDiff
                            current={getFieldDisplayValue(currentAns, "notIndicated")}
                            previous={getFieldDisplayValue(previousAns, "notIndicated")}
                          />
                        </Stack>
                        {(previousAns.notes || currentAns.notes) && (
                          <Stack gap="0">
                            <TextWrapper fw={600}>Notes</TextWrapper>
                            <WordsDiff current={currentAns.notes} previous={previousAns.notes} />
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                </Group>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}

        {/* Procedure Notes Section */}
        {(() => {
          let currentNotes: QuestionnaireResponse | undefined;
          let previousNotes: QuestionnaireResponse | undefined;

          if (versionId) {
            const currentIndex = procedureNotesHistory.findIndex((qr) => {
              const parentVersionId = getExtensionValue(
                qr,
                Extensions.QuestionnaireResponseParentVersionId,
              );
              return parentVersionId === versionId;
            });

            if (currentIndex === -1) {
              currentNotes = procedureNotesHistory[currentVersionIndex];
              previousNotes = procedureNotesHistory[currentVersionIndex + 1];
            } else {
              currentNotes = procedureNotesHistory[currentIndex];
              previousNotes = procedureNotesHistory[currentIndex + 1];
            }
          } else {
            currentNotes = procedureNotesHistory[currentVersionIndex];
            previousNotes = procedureNotesHistory[currentVersionIndex + 1];
          }

          if (!currentNotes && !previousNotes) return null;

          const currentNotesText =
            currentNotes?.item?.find((item) => item.linkId === "procedureNotes")?.answer?.[0]
              ?.valueString || "";
          const previousNotesText =
            previousNotes?.item?.find((item) => item.linkId === "procedureNotes")?.answer?.[0]
              ?.valueString || "";

          if (!currentNotesText && !previousNotesText) return null;

          return (
            <Accordion.Item key="procedureNotes" value="procedureNotes">
              <Accordion.Control>
                <Group>
                  <IconNotes size={24} color="#a0a0a0" style={{ marginRight: 8 }} />
                  <TextWrapper c="charcoal.0" variant="bold">
                    Procedure Notes
                  </TextWrapper>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Group grow align="stretch">
                  <Paper p="md" withBorder>
                    <Stack>
                      <Title mb="lg" order={4} c="gray.6">
                        Previous
                      </Title>
                      <WordsDiff
                        showRemoved
                        current={currentNotesText}
                        previous={previousNotesText}
                      />
                    </Stack>
                  </Paper>
                  <Paper p="md" withBorder>
                    <Stack>
                      <Title mb="lg" order={4} c="gray.6">
                        Amended
                      </Title>
                      <WordsDiff current={currentNotesText} previous={previousNotesText} />
                    </Stack>
                  </Paper>
                </Group>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })()}
      </Accordion>
    </Paper>
  );
};

export default NewbornProceduresDiff;
